import React from "react";
import Card from "../ui/Card";
import type { AbsentSummary, RescheduledSummary, ExcludedSummary, PendingSummary, SlaDetailGroup } from "../../types/performance";

type Props = {
  absent: AbsentSummary;
  rescheduled: RescheduledSummary;
  excluded: ExcludedSummary;
  pending: PendingSummary;
  onSelectSlaGroup: (group: SlaDetailGroup, label: string) => void;
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "18px",
  fontWeight: 700,
  color: "var(--text-primary)",
};

const sectionSubtextStyle: React.CSSProperties = {
  margin: "6px 0 16px 0",
  fontSize: "14px",
  color: "var(--text-secondary)",
};

const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "14px",
};

const itemStyle = (clickable: boolean): React.CSSProperties => ({
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "rgba(255,255,255,0.03)",
  padding: "16px",
  cursor: clickable ? "pointer" : "default",
});

const itemLabelStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
  marginBottom: "8px",
};

const itemValueStyle: React.CSSProperties = {
  fontSize: "24px",
  fontWeight: 800,
  color: "var(--text-primary)",
};

const itemMetaStyle: React.CSSProperties = {
  marginTop: "8px",
  fontSize: "12px",
  color: "var(--text-secondary)",
};

const formatInt = (value: number) => new Intl.NumberFormat("es-AR").format(value);

// Segunda fila: categorías concretas en vez de "envíos incorrectos" (esa
// card genérica ya no representa la regla de negocio — ver informe de la
// iteración de SLA). Ninguna de estas afecta el SLA general todavía (se
// reservan para SLA por zona, fuera de este alcance) — se muestran para
// contabilizar y permitir drill-down exacto.
const PerformanceIncidents = ({ absent, rescheduled, excluded, pending, onSelectSlaGroup }: Props) => {
  const cards: { key: SlaDetailGroup; label: string; value: number; meta: string }[] = [
    { key: "absentBefore21", label: "Nadie antes de 21", value: absent.before21, meta: "No afecta el SLA general." },
    { key: "absentAfter21", label: "Nadie después de 21", value: absent.after21, meta: "No afecta el SLA general." },
    { key: "rescheduledBefore21", label: "Reprogramado antes de 21", value: rescheduled.before21, meta: "No afecta el SLA general." },
    { key: "rescheduledAfter21", label: "Reprogramado después de 21", value: rescheduled.after21, meta: "No afecta el SLA general." },
    { key: "cancelled", label: "Cancelados", value: excluded.cancelled, meta: "No afecta el SLA general." },
    { key: "rejected", label: "Rechazados", value: excluded.rejected, meta: "No afecta el SLA general." },
    {
      key: "pending",
      label: "En curso / pendientes",
      value: pending.total,
      meta: `${formatInt(pending.inProgress)} en curso · ${formatInt(pending.unknown)} desconocido(s)${pending.currentDayNotClosed > 0 ? ` · ${formatInt(pending.currentDayNotClosed)} de hoy` : ""} — sin resultado final disponible.`,
    },
  ];

  return (
    <Card>
      <h2 style={sectionTitleStyle}>Nadie, reprogramados, cancelados y en curso</h2>
      <p style={sectionSubtextStyle}>
        Se contabilizan y separan por horario, pero todavía no afectan el SLA general del período — se van a usar para SLA por zona más adelante. Click en una card con datos para ver el detalle exacto.
      </p>

      <div style={gridStyle} className="performance-incidents-grid">
        {cards.map((card) => {
          const clickable = card.value > 0;
          return (
            <div
              key={card.key}
              style={itemStyle(clickable)}
              onClick={clickable ? () => onSelectSlaGroup(card.key, card.label) : undefined}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
            >
              <div style={itemLabelStyle}>{card.label}</div>
              <div style={itemValueStyle}>{formatInt(card.value)}</div>
              <div style={itemMetaStyle}>{card.meta}</div>
            </div>
          );
        })}
      </div>

      <style>
        {`
          @media (max-width: 1180px) {
            .performance-incidents-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          }
          @media (max-width: 720px) {
            .performance-incidents-grid { grid-template-columns: 1fr !important; }
          }
        `}
      </style>
    </Card>
  );
};

export default PerformanceIncidents;
